import { useEffect, useMemo, useRef, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { useBranch } from "@/context/branch";
import { matchesProductSearch, sortProductsBySearch } from "@/lib/product-search";
import { Loader2, PackageSearch, ScanBarcode, Search, X } from "lucide-react";

type Product = {
  id: number;
  name: string;
  sku?: string | null;
  brand?: string | null;
  category?: string | null;
  unit?: string | null;
  price: number | string;
  stock: number;
  isService?: boolean;
};

function BarcodeScanner({ onScan, onClose }: { onScan: (code: string) => void; onClose: () => void }) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const stopRef = useRef<{ stop: () => void } | null>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let completed = false;
    (async () => {
      try {
        const { BrowserMultiFormatReader } = await import("@zxing/browser");
        const reader = new BrowserMultiFormatReader();
        if (!videoRef.current) return;
        const controls = await reader.decodeFromVideoDevice(undefined, videoRef.current, result => {
          if (!result || completed) return;
          completed = true;
          controls?.stop();
          onScan(result.getText());
        });
        stopRef.current = controls;
      } catch {
        setError("Camera access is unavailable. You can type or scan with a connected barcode reader.");
      }
    })();
    return () => stopRef.current?.stop();
  }, [onScan]);

  return (
    <Dialog open onOpenChange={open => { if (!open) onClose(); }}>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><ScanBarcode className="h-5 w-5" /> Scan barcode</DialogTitle></DialogHeader>
        {error ? (
          <div className="rounded-lg bg-destructive/10 p-4 text-sm text-destructive">{error}</div>
        ) : (
          <div className="aspect-video overflow-hidden rounded-xl bg-black">
            <video ref={videoRef} autoPlay muted playsInline className="h-full w-full object-cover" />
          </div>
        )}
        <Button variant="outline" onClick={onClose}>Cancel</Button>
      </DialogContent>
    </Dialog>
  );
}

export default function PriceChecker() {
  const { activeBranchId, activeBranch } = useBranch();
  const [search, setSearch] = useState("");
  const [products, setProducts] = useState<Product[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [lookupError, setLookupError] = useState("");
  const [showScanner, setShowScanner] = useState(false);
  const [selected, setSelected] = useState<Product | null>(null);

  useEffect(() => {
    const query = search.trim();
    if (!query) {
      setProducts([]);
      setIsLoading(false);
      setLookupError("");
      return;
    }
    const controller = new AbortController();
    setIsLoading(true);
    setLookupError("");
    const base = import.meta.env.BASE_URL.replace(/\/$/, "");
    const params = new URLSearchParams({ search: query });
    if (activeBranchId) params.set("branchId", String(activeBranchId));
    fetch(`${base}/api/products/price-checker?${params}`, { credentials: "include", signal: controller.signal })
      .then(async response => {
        const payload = await response.json();
        if (!response.ok) throw new Error(payload.error || "Could not look up products");
        return payload as Product[];
      })
      .then(setProducts)
      .catch(error => { if (error.name !== "AbortError") setLookupError(error.message); })
      .finally(() => { if (!controller.signal.aborted) setIsLoading(false); });
    return () => controller.abort();
  }, [search, activeBranchId]);

  const results = useMemo(
    () => search.trim() ? sortProductsBySearch(products.filter(product => matchesProductSearch(product, search)), search).slice(0, 12) : [],
    [products, search],
  );

  const checkCode = (value: string) => {
    const normalized = value.trim().toLowerCase();
    setSearch(value.trim());
    const exact = products.find(product => String(product.sku ?? "").trim().toLowerCase() === normalized);
    setSelected(exact ?? null);
    setShowScanner(false);
  };

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <div>
        <h1 className="flex items-center gap-2 text-3xl font-bold tracking-tight"><ScanBarcode className="h-7 w-7 text-primary" /> Price Checker</h1>
        <p className="mt-1 text-muted-foreground">Check a product’s current selling price and availability without adding it to a sale.</p>
      </div>

      <Card>
        <CardContent className="p-5">
          <form onSubmit={event => { event.preventDefault(); checkCode(search); }} className="flex flex-col gap-3 sm:flex-row">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
              <Input autoFocus value={search} onChange={event => { setSearch(event.target.value); setSelected(null); }} placeholder="Search product name, SKU, brand or scan barcode..." className="h-12 pl-10 pr-10 text-base" />
              {search && <button type="button" onClick={() => { setSearch(""); setSelected(null); }} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"><X className="h-4 w-4" /></button>}
            </div>
            <Button type="button" variant="outline" className="h-12" onClick={() => setShowScanner(true)}><ScanBarcode className="mr-2 h-5 w-5" /> Use camera</Button>
          </form>
          {activeBranch && <p className="mt-2 text-xs text-muted-foreground">Checking prices and stock for {activeBranch.name}</p>}
        </CardContent>
      </Card>

      {lookupError ? (
        <div className="rounded-xl border border-destructive/30 bg-destructive/10 py-8 text-center text-sm text-destructive" role="alert">{lookupError}</div>
      ) : isLoading ? (
        <div className="py-20 text-center"><Loader2 className="mx-auto h-7 w-7 animate-spin text-muted-foreground" /></div>
      ) : selected ? (
        <Card className="border-primary/30">
          <CardContent className="grid gap-6 p-6 sm:grid-cols-[1fr_auto] sm:items-center">
            <div>
              <div className="mb-2 flex flex-wrap gap-2">{selected.category && <Badge variant="secondary">{selected.category}</Badge>}{selected.brand && <Badge variant="outline">{selected.brand}</Badge>}</div>
              <h2 className="text-2xl font-bold">{selected.name}</h2>
              <p className="mt-1 text-sm text-muted-foreground">SKU: {selected.sku || "Not assigned"}</p>
              <p className="mt-3 text-sm">{selected.isService ? "Service" : `${Number(selected.stock).toLocaleString()} ${selected.unit || "units"} in stock`}</p>
            </div>
            <div className="rounded-2xl bg-primary/10 px-8 py-6 text-center">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Selling price</p>
              <p className="mt-1 text-4xl font-bold text-primary">₦{Number(selected.price).toLocaleString("en-NG", { minimumFractionDigits: 2 })}</p>
            </div>
          </CardContent>
        </Card>
      ) : search.trim() ? (
        results.length ? (
          <div className="grid gap-3 md:grid-cols-2">
            {results.map(product => (
              <button key={product.id} onClick={() => setSelected(product)} className="rounded-xl border bg-background p-4 text-left transition-colors hover:border-primary hover:bg-muted/30">
                <div className="flex items-start justify-between gap-4">
                  <div><p className="font-semibold">{product.name}</p><p className="text-xs text-muted-foreground">{product.sku || "No SKU"}{product.brand ? ` · ${product.brand}` : ""}</p></div>
                  <p className="whitespace-nowrap text-lg font-bold text-primary">₦{Number(product.price).toLocaleString("en-NG", { minimumFractionDigits: 2 })}</p>
                </div>
              </button>
            ))}
          </div>
        ) : (
          <div className="rounded-xl border border-dashed py-16 text-center"><PackageSearch className="mx-auto mb-3 h-10 w-10 text-muted-foreground" /><p className="font-medium">No matching product found</p><p className="text-sm text-muted-foreground">Check the product name or barcode and try again.</p></div>
        )
      ) : (
        <div className="rounded-xl border border-dashed py-16 text-center"><ScanBarcode className="mx-auto mb-3 h-10 w-10 text-muted-foreground" /><p className="font-medium">Scan or search for a product</p><p className="text-sm text-muted-foreground">The price will appear here without starting a sale.</p></div>
      )}
      {showScanner && <BarcodeScanner onScan={checkCode} onClose={() => setShowScanner(false)} />}
    </div>
  );
}